



















import { Prop, Component, Vue, Watch } from 'vue-property-decorator';
import { Form, Button } from 'element-ui';
import LocationUpsert from '@/components/location/LocationUpsert.vue';
import ModalCard from '@/components/layout/ModalCard.vue';
import {
  CustomerLocationInput,
  CustomerLocationFields,
  GmapsDataInput,
  CustomerOrganizationFields
} from '@/types/graphql/schemaTypes';
import { convertCustomerLocationFields } from '@/types/graphql/converter';
import { customerLocationService } from '@/services/CustomerLocationService';

@Component({
  components: {
    LocationUpsert,
    ModalCard,
    'el-button': Button,
    'el-form': Form
  }
})
export default class LocationUpsertModal extends Vue {
  @Prop({ type: Object, default: () => null }) customerLocation!: CustomerLocationFields | null;
  @Prop({ type: Object, required: true }) customerOrganization!: CustomerOrganizationFields;
  @Prop({ default: false, type: Boolean }) open?: boolean;

  private customerLocationInput: CustomerLocationInput | null = null;
  private loading = false;
  private showModal = this.open;

  $refs!: {
    locationForm: HTMLFormElement;
  };

  private geocode(customerLocationInput: CustomerLocationInput) {
    const request: any = {
      address: customerLocationInput.fullAddress,
      componentRestrictions: {
        country: customerLocationInput.countryCode || null,
        postalCode: customerLocationInput.zipCode || null,
        locality: customerLocationInput.city || null
      }
    };
    // remove empty key
    Object.keys(request.componentRestrictions)
      .forEach(key => ([null, ''].indexOf(request.componentRestrictions[key]) !== -1) &&
        delete request.componentRestrictions[key]);

    const format = (prediction: any): GmapsDataInput => {
      return {
        addressComponents: JSON.stringify(prediction.address_components),
        formattedAddress: prediction.formated_address,
        placeId: prediction.place_id,
        longitude: prediction.geometry.location.lng(),
        latitude: prediction.geometry.location.lat(),
        url: prediction.url,
        name: prediction.name
      };
    };

    return new Promise<GmapsDataInput | null>((resolve, reject) => {
      try {
        const apiSuccess = this.$googleMaps.places.PlacesServiceStatus.OK;
        this.$geocoder.geocode(request, (predictions: any, status: any) => {
          if (status === apiSuccess && predictions.length === 1) {
            return resolve(format(predictions[0]));
          }

          // if we can't find precise address fall back to country postal code and city
          delete request.address;
          this.$geocoder.geocode(request, (predictions: any, status: any) => {
            if (status === apiSuccess && predictions.length === 1) {
              return resolve(format(predictions[0]));
            }
            resolve(null);
          });
        });
      } catch (e) {
        reject(e);
      }
    });
  }

  private submit(): void {
    this.$refs.locationForm.validate(async(valid: boolean) => {
      // form validation
      if (!valid || !this.customerLocationInput) {
        return;
      }
      const customerLocationInput = this.customerLocationInput;
      this.loading = true;

      try {
        // address validation
        customerLocationInput.gmapsData = await this.geocode(customerLocationInput);
        customerLocationInput.customerOrganizationId = this.customerOrganization.id;

        const response = await customerLocationService.upsertCustomerLocation(
          customerLocationInput,
          this.customerLocation ? this.customerLocation.id : undefined);
        if (response.data) {
          this.$store.dispatch('Alert/addAlert', {
            type: 'success',
            message: this.$t('success.updated'),
            time: 2000
          });

          this.$emit('input', (response.data as any).upsertCustomerLocation);
          this.customerLocationInput = null;
          this.$emit('close');
        }
      } catch (e) {
        this.$store.dispatch('Alert/addAlert', {
          type: 'error',
          message: this.$t('error.push')
        });
      } finally {
        this.loading = false;
      }
    });
  }

  @Watch('customerLocation', { immediate: true })
  private setData(customerLocation: CustomerLocationFields | null) {
    const conversionArgs = customerLocation || {} as CustomerLocationFields;
    this.customerLocationInput = convertCustomerLocationFields(conversionArgs);
  }
}
